import React from 'react';

import HubspotLogoTiny from '../../../../../../../../../src/assets/images/integrations/hubspot16.svg';
import hubspotLogoSm from '../../../../../../../../../src/assets/images/integrations/hubspot20.svg';
import hubspotLogo from '../../../../../../../../../src/assets/images/integrations/hubspot30.svg';
import {CrmToFrontCustomFieldMapping} from '../../../../../../../../../src/components/accounts/crm/common/crmSyncHelpers';
import {CrmFieldModel} from '../../../../../../../../../src/models/api/crmFieldModel';
import {
  CrmSyncAccountDefaultFieldIdsEnum,
  CrmSyncCardDefaultFieldIdsEnum,
  CrmSyncIntegrationResourceTypesEnum,
} from '../../../../../../../../../src/models/api/crmSyncModel';
import {Trans} from '../../../../../../../../shared/helpers/src/internationalization/react';
import {IconName} from '../../../../../../../../shared/ui/src/components/icon/icon';
import {CrmSyncOptionsEnum, SelectedOption} from '../crmSyncsOptions/crmSyncsOptions';
import {CrmSyncsPageConfig} from './crmSyncsPageConfig';
import {defaultIcons, maxAccountsToSync, maxCardsToSync} from './crmSyncsPageConfigHelpers';

/*
 * Constants.
 */

enum HubspotDefaultAccountFieldsEnum {
  NAME = 'name',
  DOMAINS = 'domain',
  DESCRIPTION = 'description',
}

const hubspotRequiredAccountMapping: ReadonlyArray<CrmToFrontCustomFieldMapping> = [
  {
    crmFieldId: HubspotDefaultAccountFieldsEnum.NAME,
    customFieldId: CrmSyncAccountDefaultFieldIdsEnum.NAME,
  },
];

const hubspotOptionalAccountMapping: ReadonlyArray<CrmToFrontCustomFieldMapping> = [
  {
    crmFieldId: HubspotDefaultAccountFieldsEnum.DOMAINS,
    customFieldId: CrmSyncAccountDefaultFieldIdsEnum.DOMAINS,
  },
  {
    crmFieldId: HubspotDefaultAccountFieldsEnum.DESCRIPTION,
    customFieldId: CrmSyncAccountDefaultFieldIdsEnum.DESCRIPTION,
  },
];

enum HubspotDefaultCardFieldsEnum {
  FIRST_NAME = 'firstname',
  LAST_NAME = 'lastname',
  EMAIL = 'email',
  PHONE = 'phone',
  COMPANY = 'associatedcompanyid',
}

const hubspotRequiredCardMapping: ReadonlyArray<CrmToFrontCustomFieldMapping> = [
  {
    crmFieldId: HubspotDefaultCardFieldsEnum.FIRST_NAME,
    customFieldId: CrmSyncCardDefaultFieldIdsEnum.NAME,
  },
  {
    crmFieldId: HubspotDefaultCardFieldsEnum.LAST_NAME,
    customFieldId: CrmSyncCardDefaultFieldIdsEnum.NAME,
  },
];

const hubspotOptionalCardMapping: ReadonlyArray<CrmToFrontCustomFieldMapping> = [
  {
    crmFieldId: HubspotDefaultCardFieldsEnum.EMAIL,
    customFieldId: CrmSyncCardDefaultFieldIdsEnum.EMAIL,
  },
];

enum HubspotFieldTypesEnum {
  BOOLEAN = 'boolean',
  ENUM = 'enumeration',
  STRING = 'string',
  CHECKBOX = 'checkbox',
  DATE = 'date',
  DATETIME = 'dateTime',
  NUMBER = 'number',
  BOOL_CHECKBOX = 'booleancheckbox',
  CALULATION_EQUATION = 'calculation_equation',
  RADIO = 'radio',
  SELECT = 'select', // Dropdown
  TEXT = 'text', // Plain text, displayed in single line text input.
  TEXTAREA = 'textarea', // Plain text, displayed as multi-line text input
  REFERENCE_USER = 'referenceuser',
  PHONE_NUMBER = 'phonenumber',
}

/*
 * Config.
 */

export const hubspotConfig: CrmSyncsPageConfig = {
  logo: hubspotLogo,
  logoSm: hubspotLogoSm,
  logoTiny: HubspotLogoTiny,
  displayName: 'Hubspot',
  [CrmSyncIntegrationResourceTypesEnum.ACCOUNT]: {
    helpCenterLink: 'https://help.front.com/t/y4hdac2',
    maxNumberToSync: maxAccountsToSync,
    buildExternalUrl,
    syncOptionsTitle: (
      <Trans id="crm-syncs-page-hubspot-accounts-options-title">Choose which accounts to sync</Trans>
    ),
    syncOptions: {},
    syncMapping: {
      defaultCrmFieldType: HubspotFieldTypesEnum.STRING, // if we don't have a crm field type, use string
      computeRequiredMapping: () => hubspotRequiredAccountMapping,
      computeOptionalMapping: () => hubspotOptionalAccountMapping,
      computeCrmDefaultFieldIcon: (crmField?: CrmFieldModel) =>
        computeCrmAccountDefaultFieldPlaceholderIcon(crmField?.id),
      computeCrmDefaultFieldPlaceholderName: computeCrmAccountDefaultFieldPlaceholderName,
      computeCrmDefaultFieldPlaceholderIcon: (crmFieldId: string) =>
        computeCrmAccountDefaultFieldPlaceholderIcon(crmFieldId, 'mentionSmall'),
      renderSpecialFieldMessage: (frontFieldId?: number) => {
        if (frontFieldId === CrmSyncAccountDefaultFieldIdsEnum.DOMAINS) {
          return (
            <Trans id="crm-syncs-page-accounts-mapping-domain-message">
              Contacts with matching email domains will be auto-associated with their account
            </Trans>
          );
        }
        return undefined;
      },
    },
  },
  [CrmSyncIntegrationResourceTypesEnum.CARD]: {
    helpCenterLink: 'https://help.front.com/t/y4hdac2',
    maxNumberToSync: maxCardsToSync,
    buildExternalUrl,
    syncOptionsTitle: (
      <Trans id="crm-syncs-page-hubspot-cards-options-title">Choose which contacts to sync</Trans>
    ),
    syncOptions: {
      [CrmSyncOptionsEnum.ACCOUNTS]: {
        optionText: (
          <Trans id="crm-syncs-page-hubspot-cards-sync-from-accounts-option">From synced accounts</Trans>
        ),
        tooltip: (
          <Trans id="crm-syncs-page-hubspot-cards-from-accounts-option-tooltip">
            Accounts sync must be enabled
          </Trans>
        ),
      },
      [CrmSyncOptionsEnum.LISTS]: {
        optionText: (
          <Trans id="crm-syncs-page-hubspot-cards-sync-lists-option">From a Hubspot contact list</Trans>
        ),
        tooltip: (
          <Trans id="crm-syncs-page-hubspot-cards-lists-option-tooltip">
            You can create and customize list views in Hubspot based on specific filters.
          </Trans>
        ),
        maxCountError: (
          <Trans id="crm-syncs-page-hubspot-cards-lists-option-max-count-error">
            Lists with more than {maxCardsToSync.toLocaleString()} cards cannot be synced.
          </Trans>
        ),

        dropdownPlaceholder: (
          <Trans id="crm-syncs-page-hubspot-cards-lists-option-dropdown-placeholder">Select list</Trans>
        ),
        dropdownTitle: (
          <Trans id="crm-syncs-page-hubspot-cards-lists-option-dropdown-title">Hubspot lists</Trans>
        ),
        learnMoreLink:
          'https://knowledge.hubspot.com/lists/how-can-i-create-a-list-of-all-my-hubspot-contacts',
      },
    },
    syncMapping: {
      defaultCrmFieldType: HubspotFieldTypesEnum.STRING, // if we don't have a crm field type, use string
      computeRequiredMapping: computeRequiredCardMapping,
      computeOptionalMapping: () => hubspotOptionalCardMapping,
      computeCrmDefaultFieldIcon: computeCrmCardFieldIcon,
      computeCrmDefaultFieldPlaceholderName: computeCrmCardDefaultFieldPlaceholderName,
      computeCrmDefaultFieldPlaceholderIcon: computeCrmCardDefaultFieldPlaceholderIcon,
    },
  },
};

/*
 * Helpers.
 */

function buildExternalUrl(baseUrl: string, id: string) {
  return `${baseUrl}/${id}`;
}

function computeCrmAccountDefaultFieldPlaceholderName(customFieldId: string) {
  switch (customFieldId) {
    case HubspotDefaultAccountFieldsEnum.NAME:
      return 'Account Name';
    case HubspotDefaultAccountFieldsEnum.DOMAINS:
      return 'Website';
    case HubspotDefaultAccountFieldsEnum.DESCRIPTION:
      return 'Description';
    default:
      return undefined;
  }
}

function computeCrmAccountDefaultFieldPlaceholderIcon(crmFieldId?: string, defaultIconName?: IconName) {
  switch (crmFieldId) {
    case HubspotDefaultAccountFieldsEnum.DOMAINS:
      return 'mentionSmall';
    case HubspotDefaultCardFieldsEnum.PHONE:
      return defaultIcons.phone;
    case HubspotDefaultAccountFieldsEnum.NAME:
    case HubspotDefaultAccountFieldsEnum.DESCRIPTION:
      return 'contentSmall';
    default:
      return defaultIconName;
  }
}

function computeCrmCardDefaultFieldPlaceholderName(customFieldId: string) {
  switch (customFieldId) {
    case HubspotDefaultCardFieldsEnum.FIRST_NAME:
      return 'First name';
    case HubspotDefaultCardFieldsEnum.LAST_NAME:
      return 'Last name';
    case HubspotDefaultCardFieldsEnum.COMPANY:
      return 'Company';
    default:
      return undefined;
  }
}

function computeCrmCardFieldIcon(crmField?: CrmFieldModel) {
  if (crmField?.id === HubspotDefaultCardFieldsEnum.COMPANY) {
    return 'contentSmall';
  }
  if (crmField?.id === HubspotDefaultCardFieldsEnum.EMAIL) {
    return defaultIcons.email;
  }
  if (crmField?.type === HubspotFieldTypesEnum.PHONE_NUMBER) {
    return defaultIcons.phone;
  }
  return undefined;
}

function computeCrmCardDefaultFieldPlaceholderIcon(crmFieldId?: string) {
  switch (crmFieldId) {
    case HubspotDefaultCardFieldsEnum.EMAIL:
      return defaultIcons.email;
    case HubspotDefaultCardFieldsEnum.PHONE:
      return defaultIcons.phone;
    case HubspotDefaultCardFieldsEnum.COMPANY:
    case HubspotDefaultCardFieldsEnum.FIRST_NAME:
    case HubspotDefaultCardFieldsEnum.LAST_NAME:
    default:
      return 'contentSmall';
  }
}

function computeRequiredCardMapping(selectedOption?: SelectedOption) {
  if (selectedOption?.type === CrmSyncOptionsEnum.LISTS) {
    return hubspotRequiredCardMapping;
  }
  return [
    ...hubspotRequiredCardMapping,
    {
      crmFieldId: HubspotDefaultCardFieldsEnum.COMPANY,
      customFieldId: CrmSyncCardDefaultFieldIdsEnum.ACCOUNT_ID,
    },
  ];
}
